import React, { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { pluralizeToString } from 'lib/string/pluralize'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'LuxKit/Button/TextButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import BodyText from 'LuxKit/Typography/BodyText'
import { useBedbankRoomRatePromotions } from 'hooks/OfferPage/useBedbankPromotions'
import Action from '../../Chat/MessageMeta/Action'
import LesaPurchaseSummaryMetaContainer from './Common/LesaPurchaseSummaryMetaContainer'
import { filterActions } from '../../../utils/actionsUtils'
import Modal from 'LuxKit/Modal/Modal'
import useToggle from '../../../../client/hooks/useToggle'
import BedbankPropertyFinePrint from 'components/Common/BedbankPropertyFinePrint'
import { canChangeBedbankDates, getBedbankItems } from 'supportAssistant/utils/orderUtils'
import useOffer from 'hooks/Offers/useOffer'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { buildChatMessageMetaPayload } from 'supportAssistant/utils/messageUtils'
import useOrderCancellationConfigs from 'hooks/Order/useOrderCancellationConfigs'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import { fetchExperiencesById } from 'actions/ExperienceActions'
import { fetchOffersById } from 'actions/OfferActions'

interface Props {
  order: App.Order
  isPastOrder: boolean
  isCurrentOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}
export default function LesaBedbankPurchaseSummary(props: Props) {
  const intl = useIntl()
  // verify how to render bookingInfo as we can have few rooms
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, isCurrentOrder, canEdit } = props

  const dispatch = useAppDispatch()

  const items = getBedbankItems(order)
  const item = items[0]
  const roomId = item?.rooms[0]?.roomId
  const [cancellationConfig] = useOrderCancellationConfigs(roomId, order.id)

  const experienceItems = order.experienceItems.length

  const offer = item?.offer
  const [liveOffer] = useOffer<App.Offer | App.BedbankOffer>(offer.id)

  const location = offer?.property?.address ? [offer.property.address?.city, offer.property.address?.stateProvinceName].filter(Boolean).join(', ') : undefined
  const canChangeDates = liveOffer && canChangeBedbankDates(order)
  const canBeCancelled = cancellationConfig.policyType !== 'non-refundable' && cancellationConfig.canRefund
  // bedbank inclusions
  const pkg = item.offer?.packages.find(p => p.roomId === item.roomTypeId)
  const promotions = useBedbankRoomRatePromotions(item.offer.promotions, item.checkIn.format(ISO_DATE_FORMAT), item.checkOut.format(ISO_DATE_FORMAT))

  const hasInclusions = !!promotions || !!pkg
  const hasExperiences = order.experienceItems.length > 0
  const hasTransfers = order.transferItems.length > 0

  // fine print
  const { off: closeFinePrint, value: isFinePrintOpen } = useToggle(true)

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, !hasInclusions ? ['listInclusions'] : []))

  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', {
      action: isListFlightsOrdersAction ? 'selectFlightOrder' : 'selectOrder',
      payload: {
        ...buildChatMessageMetaPayload(order, item, offer, {
          isCurrentOrder,
          canChangeDates,
          checkInDate: item.checkIn?.format(ISO_DATE_FORMAT),
          canBeCancelled,
          orderType: 'bedbank',
          canAddExtraGuests: false,
          canEditOrder: canEdit,
        }, cancellationConfig),
      },
    }))
  }, [dispatch, isListFlightsOrdersAction, order, item, offer, isCurrentOrder, canChangeDates, canBeCancelled, canEdit, cancellationConfig])

  useEffect(() => {
    if (hasExperiences || hasTransfers) {
      const experienceIds = order.experienceItems.map(item => item.experienceId)
      const transferIds = order.transferItems.map(item => item.option.experienceId)
      const allExperienceIds = [...experienceIds, ...transferIds]

      dispatch(fetchExperiencesById(...allExperienceIds))
    }

    dispatch(fetchOffersById(order.items.map(item => item.offerId)))
  }, [order, hasExperiences, hasTransfers, dispatch])

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('list-flight-orders') || !!componentsMap?.get('selectFlightOrder') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS" />}>{intl.formatMessage({
              id: 'iPTN8Dxk1j',
              defaultMessage: 'Hotel',
              description: 'Category label for bedbank hotel purchase summary',
            })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'qNm67+HE9J',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming bedbank hotel booking',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: '0AVjE2jaVU',
              defaultMessage: 'Past',
              description: 'Status label for past bedbank hotel booking',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              {location && <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{location}
              </CaptionTextBlock>}
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {offer?.name}
              </LesaPurchaseSummaryTitle>{' '}
              {items.map(item => (
                <LesaPurchaseSummaryItineraryDates
                  key={item.id}
                  status={item.status}
                  startDate={item.checkIn?.format(ISO_DATE_FORMAT)}
                  endDate={item.checkOut?.format(ISO_DATE_FORMAT)}
                  duration={pluralizeToString('night', item.duration ?? 0)}
                  showReservationDates
                />
              ))}
              <LesaPurchaseSummarySeparator>
                <BodyText as="span" variant="small" colour="neutral-three">{formatOccupantsShort(item?.rooms[0]?.occupancy)}</BodyText>
                {!!items.length && <BodyText as="span" variant="small" colour="neutral-three">{pluralizeToString('room', item?.rooms?.length)}</BodyText>}
                {!!experienceItems && <BodyText as="span" variant="small" colour="neutral-three">{pluralizeToString('experience', experienceItems)}</BodyText>}
              </LesaPurchaseSummarySeparator>
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: 'HNTteXYOw6',
                  defaultMessage: 'Select',
                  description: 'Button text to select a bedbank hotel booking',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!componentsMap?.get('listInclusions') && (
        <LesaPurchaseSummaryMetaContainer>
          <BodyText variant="medium" weight="bold">{intl.formatMessage({
            id: 'l4CWQ17+2b',
            defaultMessage: 'Package inclusions:',
            description: 'Bold label for package inclusions section',
          })}
          </BodyText>
          {item?.rooms[0]?.facilities.map(facility => (
            <OfferInclusionItem
              key={facility.id}
              inclusion={facility}
            />
          ))}
        </LesaPurchaseSummaryMetaContainer>
      )}
      {!!componentsMap?.get('fine-print-modal') && (
        <Modal
          isOpen={isFinePrintOpen}
          onClose={closeFinePrint}
          size="L"
          title={intl.formatMessage({
            id: 'rzorNj/CcC',
            defaultMessage: 'Fine print',
            description: 'Title for the fine print modal',
          })}
        >
          {offer && <BedbankPropertyFinePrint
            finePrint={offer.property.finePrint!}
          />}
        </Modal>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} order={order} />
          ))}
        </Group>}
    </>
  )
}
